import React, { useMemo, useState } from 'react';

export default function Legend({ minerals = [], crops = [], selectedDistrict = null, selectedDetails = { minerals: [], crops: [], region: null } }) {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('overview');

  const filteredMinerals = useMemo(() => {
    const term = q.trim().toLowerCase();
    return minerals.filter(m => !term || m.name.toLowerCase().includes(term));
  }, [minerals, q]);

  const filteredCrops = useMemo(() => {
    const term = q.trim().toLowerCase();
    return crops.filter(c => !term || c.name.toLowerCase().includes(term) || (c.region || '').toLowerCase().includes(term));
  }, [crops, q]);

  // image candidate helper for crops/minerals (same as map)
  const imageCandidates = (name, folder = 'minerals') => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const nameSlug = name.replace(/[()\s\/\\,]+/g, '-').replace(/[^a-z0-9-]+/gi, '').toLowerCase();
    return [
      `/images/${folder}/${slug}.jpeg`,
      `/images/${folder}/${slug}.jpg`,
      `/images/${folder}/${slug}.png`,
      `/images/${folder}/${nameSlug}.jpeg`,
      `/images/${folder}/${nameSlug}.jpg`,
      `/images/${folder}/${nameSlug}.png`,
      `/images/${folder}/${slug}.svg`,
      `/images/${folder}/placeholder.svg`
    ];
  };

  const imgPropsFor = (name, folder = 'minerals') => {
    const candidates = imageCandidates(name, folder);
    return {
      src: candidates[0],
      'data-candidates': JSON.stringify(candidates),
      'data-attempt': '0',
      alt: name,
      loading: 'lazy',
      decoding: 'async',
      className: 'img-thumb',
      onError: (e) => {
        const el = e.currentTarget;
        try {
          const list = JSON.parse(el.dataset.candidates || '[]');
          let attempt = parseInt(el.dataset.attempt || '0', 10);
          attempt = Math.min(list.length - 1, attempt + 1);
          el.src = list[attempt];
          el.dataset.attempt = String(attempt);
          if (attempt === list.length - 1 && list[attempt].endsWith('placeholder.svg')) {
            console.warn(`Image not found for "${name}" in /images/${folder}/ — used placeholder.`);
          }
        } catch {
          el.src = `/images/${folder}/placeholder.svg`;
        }
      }
    };
  };

  const selMinerals = selectedDetails.minerals || [];
  const selCrops = selectedDetails.crops || [];
  const hasMinerals = selMinerals.length > 0;
  const hasCrops = selCrops.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="legend-title">Legend & Details</h3>
        </div>
        <div className="text-xs text-slate-400">{selectedDistrict || (selectedDetails.region || '—')}</div>
      </div>

      <div className="mb-3">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search minerals, crops or regions..."
          className="w-full p-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200"
        />
      </div>

      <div className="flex gap-2 mb-3">
        <button onClick={() => setTab('overview')} className={`flex-1 py-2 rounded-lg ${tab === 'overview' ? 'bg-indigo-600 text-white shadow' : 'bg-white'}`}>Overview</button>
        <button onClick={() => setTab('minerals')} className={`flex-1 py-2 rounded-lg ${tab === 'minerals' ? 'bg-indigo-600 text-white shadow' : 'bg-white'}`}>Minerals</button>
        <button onClick={() => setTab('crops')} className={`flex-1 py-2 rounded-lg ${tab === 'crops' ? 'bg-indigo-600 text-white shadow' : 'bg-white'}`}>Crops</button>
      </div>

      {tab === 'overview' && (
        <>
          {hasMinerals && !hasCrops && (
            // only minerals: full-width list
            <div className="space-y-2 legend-scroll">
              {selMinerals.map(m => (
                <div key={m._id} className="list-item card">
                  <img {...imgPropsFor(m.name, 'minerals')} />
                  <div>
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-slate-500">{m.use}</div>
                    <div className="text-xs text-slate-400 mt-1">Districts: {(m.districts||[]).join(', ')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasCrops && !hasMinerals && (
            // only crops: full-width list
            <div className="space-y-2 legend-scroll">
              {selCrops.map(c => (
                <div key={c._id} className="list-item card">
                  <img {...imgPropsFor(c.name, 'crops')} />
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.region} — {c.use}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasMinerals && hasCrops && (
            // both available: two-column split (minerals | crops)
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card legend-scroll">
                <div className="text-sm text-gray-600 font-semibold mb-2">Minerals</div>
                <div className="space-y-2">
                  {selMinerals.map(m => (
                    <div key={m._id} className="list-item">
                      <img {...imgPropsFor(m.name, 'minerals')} />
                      <div>
                        <div className="font-medium">{m.name}</div>
                        <div className="text-xs text-slate-500">{m.use}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card legend-scroll">
                <div className="text-sm text-gray-600 font-semibold mb-2">Crops</div>
                <div className="space-y-2">
                  {selCrops.map(c => (
                    <div key={c._id} className="list-item">
                      <img {...imgPropsFor(c.name, 'crops')} />
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.region} — {c.use}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!hasMinerals && !hasCrops && (
            // no selection: show defaults / search results in two columns
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="card legend-scroll">
                <div className="text-sm text-gray-600 font-semibold mb-2">Minerals</div>
                <div className="space-y-2">
                  {filteredMinerals.map(m => (
                    <div key={m._id} className="list-item">
                      <img {...imgPropsFor(m.name, 'minerals')} />
                      <div>
                        <div className="font-medium">{m.name}</div>
                        <div className="text-xs text-slate-500">{m.use}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card legend-scroll">
                <div className="text-sm text-gray-600 font-semibold mb-2">Crops</div>
                <div className="space-y-2">
                  {filteredCrops.map(c => (
                    <div key={c._id} className="list-item">
                      <img {...imgPropsFor(c.name, 'crops')} />
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.region} — {c.use}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'minerals' && (
        <div className="space-y-3 max-h-[48vh] overflow-auto">
          {filteredMinerals.map(m => (
            <div key={m._id} className="list-item card">
              <img {...imgPropsFor(m.name, 'minerals')} />
              <div>
                <div className="font-semibold">{m.name}</div>
                <div className="text-xs text-slate-500">Districts: {(m.districts || []).join(', ')}</div>
                <div className="text-xs text-slate-500 mt-1">{m.use}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'crops' && (
        <div className="space-y-3 max-h-[48vh] overflow-auto">
          {filteredCrops.map(c => (
            <div key={c._id} className="list-item card">
              <img {...imgPropsFor(c.name, 'crops')} />
              <div>
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-slate-500">{c.region} — {c.use}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
